/* Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import {
  BusinessContextKpiQuery,
  BusinessContextTargetsQuery,
} from "~/generated/graphql";
import { KpiType } from "~/constants";
import { convertToPercent, formatMoney } from "~/tools";

export function mapBusinessContextKpi(
  kpiType: KpiType,
  data?: BusinessContextKpiQuery
) {
  if (!data) {
    return;
  }

  const kpiDetails = getKpiDetails(kpiType, data);
  const currentValueRaw = kpiDetails?.metrics?.currentValue;

  return {
    currentValue: currentValueRaw
      ? convertToPercent(currentValueRaw)
      : undefined,
    targetValue: kpiDetails?.targets?.currentTarget?.goal,
    timePoints: kpiDetails?.metrics?.values ?? [],
    targets: kpiDetails?.targets?.targets?.map(mapTargets) ?? [],
  };
}

type KpiTarget = {
  goal: number;
  startDate: string;
  endDate: string;
  name: string;
};

function mapTargets({ goal, startDate, endDate, name }: KpiTarget) {
  return {
    name,
    value: goal.toString(),
    startDate,
    endDate,
  };
}

function getKpiDetails(kpiType: KpiType, data?: BusinessContextKpiQuery) {
  const kpis = data?.businessContext?.kpis;

  switch (kpiType) {
    case KpiType.COST_ALLOCATION_COVERAGE:
      return kpis?.costAllocationCoverage?.details;
    case KpiType.NON_PROD_HOURS:
      return kpis?.nonProdHours?.details;
    case KpiType.OVERALL_WASTE:
      return kpis?.overallWaste?.details;
    default:
      return;
  }
}

export function mapKpiTargets(
  kpiType: KpiType,
  data?: BusinessContextTargetsQuery
) {
  if (!data) {
    return [];
  }
  return getKpiTargets(kpiType, data) ?? [];
}

function getKpiTargets(kpiType: KpiType, data?: BusinessContextTargetsQuery) {
  const kpis = data?.businessContext?.kpis;

  switch (kpiType) {
    case KpiType.COST_ALLOCATION_COVERAGE:
      return kpis?.costAllocationCoverage?.targets;
    case KpiType.NON_PROD_HOURS:
      return kpis?.nonProdHours?.targets;
    case KpiType.OVERALL_WASTE:
      return kpis?.overallWaste?.targets;
    default:
      return;
  }
}

export function mapKpiCurrentTarget(
  kpiType: KpiType,
  data?: BusinessContextKpiQuery
) {
  if (!data) {
    return;
  }

  return getKpiDetails(kpiType, data)?.targets?.currentTarget;
}

export function mapWorstOrgUnits(
  currency: string,
  data?: BusinessContextKpiQuery
) {
  const kpis = data?.businessContext?.kpis;

  const worstWaste = kpis?.overallWaste?.worst?.orgUnits?.map(
    ({ orgUnit: { name, nid }, kpiValue, currentWaste }) => ({
      name,
      nid,
      value: `${convertToPercent(kpiValue)}%`,
      additionalValue: formatMoney(currentWaste, currency),
    })
  );

  const worstNonProdHours =
    kpis?.nonProdHours?.worst?.orgUnits?.map(
      ({ orgUnit: { name, nid }, kpiValue, hours }) => ({
        name,
        nid,
        value: `${convertToPercent(kpiValue)}%`,
        additionalValue: `${hours}h`,
      })
    ) ?? [];

  return worstWaste ?? worstNonProdHours;
}
